import { useEffect, useMemo, useState } from 'react';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { milestoneFor } from '../components/MilestoneModal';

// Threshold for the once-per-account "invite a friend" nudge. Sits between
// the 25- and 50-artist exploration milestones so it never piles on top of
// another celebration, and is late enough that the user has clearly
// committed to Stellar without being so late most users never see it.
export const FRIEND_INVITE_THRESHOLD = 40;

// Returns whatever celebration popup should be shown on the calling canvas
// right now. Three kinds, with this priority when more than one is pending:
//   1. `firstDiscoveryActive` — first artist ever marked as discovered.
//   2. `friendInviteActive`   — 40+ explored and still zero friends.
//   3. `activeMilestone`      — an exploration-count milestone (1, 10, ...).
//
// Each is persisted server-side so dismissing one suppresses it forever;
// the queue advances as the user's state updates.
export function useMilestoneTrigger() {
  const { user, isLoading, favorites, dislikes, knownArtists, discoveredArtists, friends } = useAuth();
  const { setLastMilestoneShown, setFirstDiscoveryShown, setFriendInviteShown } = useAuthActions();

  // Union of every artist marking — matches the "X explored" badge in the
  // header (LandingPage exploredCount).
  const exploredCount = useMemo(() => {
    if (!user) return 0;
    const set = new Set();
    for (const f of favorites) set.add(f.artistName);
    for (const d of dislikes) set.add(d.artistName);
    for (const k of knownArtists) set.add(k.artistName);
    for (const dd of discoveredArtists) set.add(dd.artistName);
    return set.size;
  }, [user, favorites, dislikes, knownArtists, discoveredArtists]);

  const discoveredCount = discoveredArtists?.length || 0;
  const friendCount = friends?.length || 0;
  const lastShown = user?.lastMilestoneShown || 0;
  const firstDiscoveryAlreadyShown = !!user?.firstDiscoveryShown;
  const friendInviteAlreadyShown = !!user?.friendInviteShown;

  const pendingMilestone = useMemo(
    () => milestoneFor(exploredCount, lastShown),
    [exploredCount, lastShown]
  );
  const pendingFirstDiscovery = !firstDiscoveryAlreadyShown && discoveredCount >= 1;
  const pendingFriendInvite =
    !friendInviteAlreadyShown && friendCount === 0 && exploredCount >= FRIEND_INVITE_THRESHOLD;

  const [activeMilestone, setActiveMilestone] = useState(0);
  const [firstDiscoveryActive, setFirstDiscoveryActive] = useState(false);
  const [friendInviteActive, setFriendInviteActive] = useState(false);

  // Don't show anything until initial auth/data fetch finishes — otherwise
  // we can flash a popup against an empty list while data is loading.
  useEffect(() => {
    if (isLoading || !user) {
      setActiveMilestone(0);
      setFirstDiscoveryActive(false);
      setFriendInviteActive(false);
      return;
    }
    // Show one popup at a time; the others wait their turn until their
    // upstream flag flips. Priority: first-discovery > friend-invite > milestone.
    if (pendingFirstDiscovery) {
      setFirstDiscoveryActive(true);
    } else if (pendingFriendInvite) {
      setFriendInviteActive(true);
    } else if (pendingMilestone > 0) {
      setActiveMilestone(pendingMilestone);
    }
  }, [pendingFirstDiscovery, pendingFriendInvite, pendingMilestone, isLoading, user]);

  const dismissMilestone = () => {
    if (activeMilestone > 0) {
      setLastMilestoneShown(activeMilestone);
      setActiveMilestone(0);
    }
  };

  const dismissFirstDiscovery = () => {
    if (firstDiscoveryActive) {
      setFirstDiscoveryShown();
      setFirstDiscoveryActive(false);
      // Effect re-runs once `user.firstDiscoveryShown` flips and surfaces
      // any queued friend-invite or exploration milestone.
    }
  };

  const dismissFriendInvite = () => {
    if (friendInviteActive) {
      setFriendInviteShown();
      setFriendInviteActive(false);
    }
  };

  return {
    activeMilestone,
    dismissMilestone,
    firstDiscoveryActive,
    dismissFirstDiscovery,
    friendInviteActive,
    dismissFriendInvite,
  };
}
